/**
 * Square-grid ripple behind the navbar Free Quote button.
 * Ports Kimi Code console's useGridBreathing (k3-card canvas).
 */

const DESKTOP_MEDIA = '(min-width: 990px)';
const FALLBACK_RGB = [32, 150, 243];

function cssColorToRgb(value, ctx) {
  if (!value || !ctx) return null;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = '#000';
  ctx.fillStyle = value;
  ctx.fillRect(0, 0, 1, 1);
  const data = ctx.getImageData(0, 0, 1, 1).data;
  if (!data[3]) return null;
  return [data[0], data[1], data[2]];
}

const PARAMS = {
  cell: 7,
  gap: 2,
  idle: 0.02,
  hoverBlue: 0.28,
  rippleGain: 1.25,
  rippleSpeed: 180,
  follow: 1.1,
  followR: 14,
  btnRadius: 380,
  falloff: 2.4,
  loopInterval: 4.2,
};

class PaQuoteRipple extends HTMLElement {
  constructor() {
    super();
    this._canvas = null;
    this._cta = null;
    this._ctx = null;
    this._raf = 0;
    this._loopTimer = 0;
    this._resizeObserver = null;
    this._media = window.matchMedia(DESKTOP_MEDIA);
    this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._cells = [];
    this._ripples = [];
    this._gridCW = 7;
    this._gridCH = 7;
    this._hoverT = 0;
    this._isHover = false;
    this._btnCX = 0;
    this._btnCY = 0;
    this._boxW = 0;
    this._boxH = 0;
    this._pmX = -9999;
    this._pmY = -9999;
    this._running = false;
    this._rippleRaw = '';
    this._rippleRgb = FALLBACK_RGB;
    this._colorProbeCtx = null;
    this._onMediaChange = this._onMediaChange.bind(this);
    this._onCtaEnter = this._onCtaEnter.bind(this);
    this._onCtaLeave = this._onCtaLeave.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._loop = this._loop.bind(this);
  }

  connectedCallback() {
    this._canvas = this.querySelector('[data-ripple-canvas]');
    this._cta = this.querySelector('[data-ripple-cta]');
    if (!(this._canvas instanceof HTMLCanvasElement) || !this._cta) return;

    this._cta.addEventListener('pointerenter', this._onCtaEnter);
    this._cta.addEventListener('pointerleave', this._onCtaLeave);
    this._cta.addEventListener('focus', this._onCtaEnter);
    this._cta.addEventListener('blur', this._onCtaLeave);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerleave', this._onPointerLeave);
    this._media.addEventListener('change', this._onMediaChange);
    this._motion.addEventListener('change', this._onMediaChange);

    this._resizeObserver = new ResizeObserver(() => this._sizeDots());
    this._resizeObserver.observe(this._canvas);
    this._resizeObserver.observe(this._cta);

    this._onMediaChange();
  }

  disconnectedCallback() {
    this._cta?.removeEventListener('pointerenter', this._onCtaEnter);
    this._cta?.removeEventListener('pointerleave', this._onCtaLeave);
    this._cta?.removeEventListener('focus', this._onCtaEnter);
    this._cta?.removeEventListener('blur', this._onCtaLeave);
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerleave', this._onPointerLeave);
    this._media.removeEventListener('change', this._onMediaChange);
    this._motion.removeEventListener('change', this._onMediaChange);
    this._resizeObserver?.disconnect();
    this._stop(true);
  }

  _onMediaChange() {
    if (this._media.matches && !this._motion.matches) {
      this._sizeDots();
      if (!this._running) this._drawStatic();
    } else {
      this._stop(true);
    }
  }

  _onCtaEnter() {
    if (!this._media.matches || this._motion.matches) return;
    if (this._isHover) return;
    this._isHover = true;
    this._pushRipple();
    this._armLoop();
    this._start();
  }

  _onCtaLeave() {
    if (this._cta?.matches(':hover, :focus-visible')) return;
    this._isHover = false;
    this._clearLoop();
  }

  _onPointerMove(event) {
    const rect = this._canvas.getBoundingClientRect();
    this._pmX = event.clientX - rect.left;
    this._pmY = event.clientY - rect.top;
  }

  _onPointerLeave() {
    this._pmX = -9999;
    this._pmY = -9999;
  }

  _armLoop() {
    this._clearLoop();
    const tick = () => {
      this._pushRipple();
      this._loopTimer = window.setTimeout(tick, PARAMS.loopInterval * 1000);
    };
    this._loopTimer = window.setTimeout(tick, PARAMS.loopInterval * 1000);
  }

  _clearLoop() {
    if (this._loopTimer) {
      clearTimeout(this._loopTimer);
      this._loopTimer = 0;
    }
  }

  _pushRipple() {
    this._ripples.push({
      x: this._btnCX,
      y: this._btnCY,
      t0: performance.now() / 1000,
    });
  }

  _start() {
    if (this._running) return;
    this._running = true;
    this._raf = requestAnimationFrame(this._loop);
  }

  _stop(clearCanvas) {
    this._running = false;
    this._clearLoop();
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
    this._ripples = [];
    this._isHover = false;
    this._hoverT = 0;
    if (clearCanvas) this._clear();
    else this._drawStatic();
  }

  _clear() {
    if (!this._ctx) return;
    this._ctx.clearRect(0, 0, this._boxW, this._boxH);
  }

  _sizeDots() {
    const canvas = this._canvas;
    if (!(canvas instanceof HTMLCanvasElement)) return;
    if (!this._media.matches || this._motion.matches) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this._ctx = ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cols = Math.max(1, Math.round(rect.width / PARAMS.cell));
    const rows = Math.max(1, Math.round(rect.height / PARAMS.cell));
    this._gridCW = rect.width / cols;
    this._gridCH = rect.height / rows;
    this._boxW = rect.width;
    this._boxH = rect.height;

    const ctaRect = this._cta.getBoundingClientRect();
    this._btnCX = ctaRect.left - rect.left + ctaRect.width / 2;
    this._btnCY = ctaRect.top - rect.top + ctaRect.height / 2;

    this._cells = [];
    for (let iy = 0; iy < rows; iy++) {
      for (let ix = 0; ix < cols; ix++) {
        this._cells.push({ x: ix * this._gridCW, y: iy * this._gridCH });
      }
    }

    if (!this._running) this._drawStatic();
  }

  _rippleGlow(x, y, t) {
    let glow = 0;
    for (const rp of this._ripples) {
      const age = t - rp.t0;
      const life = 1.15;
      const u = Math.min(1, age / Math.max(0.001, life));
      const radius = 24 + PARAMS.rippleSpeed * life * (2 * u - u * u);
      const delta = (Math.hypot(x - rp.x, y - rp.y) || 1) - radius;
      const g = Math.exp(-(delta * delta) / 1352);
      const decay = Math.max(0, 1 - age / life);
      glow = Math.max(glow, g * decay);
    }
    return glow;
  }

  _cellMask(cx, cy) {
    const distB = Math.hypot(cx - this._btnCX, cy - this._btnCY);
    if (distB >= PARAMS.btnRadius) return 0;
    const n = distB / PARAMS.btnRadius;
    return (1 - n * n * (3 - 2 * n)) ** PARAMS.falloff;
  }

  _drawFrame(t) {
    const ctx = this._ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this._boxW, this._boxH);

    const gw = this._gridCW;
    const gh = this._gridCH;
    const gp = PARAMS.gap;
    const [br, bg, bb] = this._readRippleRgb();
    const followVar = 2 * PARAMS.followR * PARAMS.followR;

    for (const cell of this._cells) {
      const cx = cell.x + gw / 2;
      const cy = cell.y + gh / 2;
      const mask = this._cellMask(cx, cy);
      if (mask <= 0) continue;

      let blue = PARAMS.idle;
      blue += this._hoverT * PARAMS.hoverBlue;

      const fdx = cx - this._pmX;
      const fdy = cy - this._pmY;
      blue += Math.exp(-(fdx * fdx + fdy * fdy) / followVar) * PARAMS.follow * PARAMS.rippleGain * this._hoverT;
      blue += this._rippleGlow(cx, cy, t) * PARAMS.rippleGain;
      blue = Math.min(0.78, blue) * mask;
      if (blue < 0.02) continue;

      ctx.fillStyle = `rgba(${br},${bg},${bb},${blue.toFixed(3)})`;
      ctx.fillRect(cell.x + gp / 2, cell.y + gp / 2, gw - gp, gh - gp);
    }
  }

  _readRippleRgb() {
    const styles = getComputedStyle(this);
    const raw = (
      styles.getPropertyValue('--pa-ripple') ||
      styles.getPropertyValue('--pa-accent') ||
      styles.getPropertyValue('--color-primary-button-background')
    ).trim();

    if (raw && raw === this._rippleRaw && this._rippleRgb) return this._rippleRgb;

    this._rippleRaw = raw;
    this._rippleRgb = cssColorToRgb(raw, this._colorProbe()) || FALLBACK_RGB;
    return this._rippleRgb;
  }

  _colorProbe() {
    if (this._colorProbeCtx) return this._colorProbeCtx;
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    this._colorProbeCtx = canvas.getContext('2d', { willReadFrequently: true });
    return this._colorProbeCtx;
  }

  _drawStatic() {
    this._drawFrame(0);
  }

  _loop(now) {
    if (!this._running) return;
    const t = now / 1000;
    const target = this._isHover ? 1 : 0;
    this._hoverT += 0.06 * (target - this._hoverT);
    this._ripples = this._ripples.filter((rp) => t - rp.t0 < 1.3);
    this._drawFrame(t);

    const stillAnimating = this._isHover || this._ripples.length > 0 || this._hoverT > 0.01;
    if (stillAnimating) {
      this._raf = requestAnimationFrame(this._loop);
    } else {
      this._running = false;
      this._raf = 0;
      this._hoverT = 0;
      this._drawStatic();
    }
  }
}

if (!customElements.get('pa-quote-ripple')) {
  customElements.define('pa-quote-ripple', PaQuoteRipple);
}
